/**
 * Step 3 of public registration — collect company details, choose a tier,
 * accept EULA, complete the saga, and redirect to /login.
 *
 * Path: /register/complete?pendingId=…
 */
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PendoEvents } from '@/platform/analytics/pendoEvents'
import { publicRegistrationApi } from '../api/public'
import type {
  CompanyKind,
  EulaVersionDto,
  SubscriptionTierDto,
  TierCode,
} from '@shared/types/registration'

const COMPANY_KINDS: { value: CompanyKind; label: string }[] = [
  { value: 'dealer', label: 'Dealer' },
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'distributor', label: 'Distributor' },
  { value: 'rep', label: 'Manufacturer rep' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'customer', label: 'Customer' },
]

const REGIONS: { value: 'NA' | 'UK'; label: string; currency: string }[] = [
  { value: 'NA', label: 'North America', currency: 'USD' },
  { value: 'UK', label: 'United Kingdom', currency: 'GBP' },
]

export const RegistrationCompletePage = (): JSX.Element => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const pendingId = params.get('pendingId') ?? ''

  const [companyName, setCompanyName] = useState('')
  const [kind, setKind] = useState<CompanyKind>('dealer')
  const [region, setRegion] = useState<'NA' | 'UK'>('NA')
  const [tier, setTier] = useState<TierCode>('starter')
  const [eulaAgreed, setEulaAgreed] = useState(false)
  const [marketingOptIn, setMarketingOptIn] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [phone, setPhone] = useState('')

  const [tiers, setTiers] = useState<SubscriptionTierDto[]>([])
  const [eula, setEula] = useState<EulaVersionDto | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const currency = useMemo(
    () => REGIONS.find((r) => r.value === region)?.currency ?? 'USD',
    [region],
  )

  useEffect(() => {
    void publicRegistrationApi.listTiers().then(setTiers).catch(() => undefined)
  }, [])

  useEffect(() => {
    void publicRegistrationApi
      .getEulaForRegion(region)
      .then(setEula)
      .catch(() => setEula(null))
  }, [region])

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setError(null)
    if (!eula) {
      setError('EULA could not be loaded; please refresh and try again.')
      return
    }
    if (!eulaAgreed) {
      setError('You must agree to the EULA to continue.')
      return
    }
    setBusy(true)
    try {
      await publicRegistrationApi.complete({
        pendingId,
        company: {
          name: companyName,
          kind,
          region,
          defaultCurrency: currency,
          defaultLocale: region === 'UK' ? 'en-GB' : 'en-US',
        },
        primaryUser: {
          firstName,
          lastName,
          jobTitle: jobTitle || null,
          phone: phone || null,
        },
        tier,
        eulaVersionId: eula.id,
        marketingOptIn,
      })
      PendoEvents.registrationStep({ step: 'completed' })
      navigate('/login?registered=1')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setBusy(false)
    }
  }

  if (!pendingId) {
    return (
      <Centered title="Missing context">
        <Alert variant="destructive">
          <AlertDescription>
            This page must be opened from the verification email.
          </AlertDescription>
        </Alert>
      </Centered>
    )
  }

  return (
    <Centered title="Tell us about your business" maxW="max-w-2xl">
      <form onSubmit={onSubmit} className="space-y-6">
        <Section title="Company">
          <Input
            placeholder="Company name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            required
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select value={kind} onValueChange={(v) => setKind(v as CompanyKind)}>
              <SelectTrigger>
                <SelectValue placeholder="Company type" />
              </SelectTrigger>
              <SelectContent>
                {COMPANY_KINDS.map((k) => (
                  <SelectItem key={k.value} value={k.value}>
                    {k.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={region} onValueChange={(v) => setRegion(v as 'NA' | 'UK')}>
              <SelectTrigger>
                <SelectValue placeholder="Region" />
              </SelectTrigger>
              <SelectContent>
                {REGIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label} · {r.currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Section>

        <Section title="Primary contact">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
            <Input
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
          <Input
            placeholder="Job title (optional)"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
          />
          <Input
            placeholder="Phone (optional)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            type="tel"
          />
        </Section>

        <Section title="Plan">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tiers.map((t) => (
              <button
                type="button"
                key={t.id}
                onClick={() => setTier(t.code)}
                className={`text-left border rounded p-3 hover:bg-muted ${
                  tier === t.code ? 'border-primary bg-muted' : ''
                }`}
              >
                <div className="flex flex-wrap justify-between items-center gap-2">
                  <span className="font-medium min-w-0 truncate">{t.displayName}</span>
                  <Badge variant="outline" className="shrink-0">${(t.monthlyPriceUsdCents / 100).toFixed(0)}/mo</Badge>
                </div>
                {t.description && (
                  <p className="text-xs text-muted-foreground mt-1">{t.description}</p>
                )}
                {t.trialDays > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t.trialDays}-day free trial
                  </p>
                )}
              </button>
            ))}
          </div>
        </Section>

        <Section title="Terms">
          {eula ? (
            <div className="border rounded p-3 bg-muted/30 max-h-48 overflow-y-auto text-xs whitespace-pre-wrap">
              <div className="text-muted-foreground mb-1">
                EULA v{eula.version} · {eula.region}
              </div>
              {eula.bodyText ?? '(see linked document)'}
            </div>
          ) : (
            <div
              className="space-y-2"
              role="status"
              aria-busy="true"
              aria-live="polite"
              aria-label="Loading EULA"
            >
              <Skeleton className="h-3 w-full rounded" />
              <Skeleton className="h-3 w-full rounded" />
              <Skeleton className="h-3 w-2/3 rounded" />
            </div>
          )}
          <label className="flex items-start gap-2 text-sm mt-3">
            <Checkbox
              checked={eulaAgreed}
              onCheckedChange={(v) => setEulaAgreed(!!v)}
              required
            />
            <span>I agree to the EULA above.</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={marketingOptIn}
              onCheckedChange={(v) => setMarketingOptIn(!!v)}
            />
            <span>Send me occasional product updates.</span>
          </label>
        </Section>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" className="w-full" disabled={busy || !eula}>
          {busy ? 'Creating workspace…' : 'Create my workspace'}
        </Button>
      </form>
    </Centered>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}): JSX.Element {
  return (
    <div className="space-y-2">
      <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
        {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  )
}

function Centered({
  title,
  children,
  maxW = 'max-w-md',
}: {
  title: string
  children: React.ReactNode
  maxW?: string
}): JSX.Element {
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className={`w-full ${maxW}`}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  )
}
